import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as contractsApi from '../../api/contracts'
import * as linksApi from '../../api/links'
import * as attachmentsApi from '../../api/attachments'
import * as activityLogsApi from '../../api/activityLogs'
import { useUIStore } from '../../stores/uiStore'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Badge from '../../components/common/Badge'
import Spinner from '../../components/common/Spinner'
import PDFViewer from '../../components/common/PDFViewer'
import SendSigningLinkModal from '../../components/contracts/SendSigningLinkModal'
import LinkTrainerModal from '../../components/contracts/LinkTrainerModal'
import LinkTeamToContractModal from '../../components/contracts/LinkTeamToContractModal'
import PaymentSchedulePreview from '../../components/contracts/PaymentSchedulePreview'
import PaymentScheduleManager from '../../components/contracts/PaymentScheduleManager'
import { calculatePayments } from '../../utils/paymentCalculator'
import {
  DocumentTextIcon,
  UserGroupIcon,
  UsersIcon,
  ClockIcon,
  PaperAirplaneIcon,
  ArrowDownTrayIcon,
  BellAlertIcon,
  TrashIcon,
  UserPlusIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
  EyeIcon,
  EyeSlashIcon,
  CurrencyDollarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

/**
 * Calculate contract duration information
 * @param {Object} contract - Contract object with start_date and end_date
 * @returns {Object|null} Duration information or null if dates are missing
 */
function calculateContractDuration(contract) {
  if (!contract.start_date || !contract.end_date) return null
  
  const now = new Date()
  const startDate = new Date(contract.start_date)
  const endDate = new Date(contract.end_date)
  
  // Days running (from start to now, or start to end if not started yet)
  const effectiveStart = now > startDate ? startDate : now
  const daysRunning = Math.max(0, Math.floor((now - effectiveStart) / (1000 * 60 * 60 * 24)))
  
  // Days until end
  const daysUntilEnd = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)))
  
  // Total contract duration
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
  
  return {
    daysRunning,
    daysUntilEnd,
    totalDays,
    hasStarted: now >= startDate,
    hasEnded: now >= endDate
  }
}

/**
 * Contract Detail Page
 * View and manage a single contract with tabbed interface
 */
export default function ContractDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [searchParams] = useSearchParams()
  
  const [activeTab, setActiveTab] = useState('overview')
  const [showSendLinkModal, setShowSendLinkModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [showLinkTrainerModal, setShowLinkTrainerModal] = useState(false)
  const [showLinkTeamModal, setShowLinkTeamModal] = useState(false)

  // Check for tab query parameter on mount
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['overview', 'trainers', 'payments', 'activity', 'files'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  // Fetch full contract details
  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract', id, 'full'],
    queryFn: () => contractsApi.getContractFull(id),
  })

  // Download contract mutation
  const downloadMutation = useMutation({
    mutationFn: () => contractsApi.downloadContract(id),
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${contract?.title || 'contract'}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      showToast('Contract downloaded successfully', 'success')
    },
    onError: (error) => {
      showToast(error.message || 'Failed to download contract', 'error')
    },
  })

  // Delete contract mutation
  const deleteMutation = useMutation({
    mutationFn: () => contractsApi.deleteContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['contracts'])
      queryClient.invalidateQueries(['dashboard-stats'])
      showToast('Contract deleted successfully', 'success')
      navigate('/contracts')
    },
    onError: (error) => {
      showToast(error.message || 'Failed to delete contract', 'error')
    },
  })

  // Unlink trainer mutation
  const unlinkTrainerMutation = useMutation({
    mutationFn: (trainerId) => linksApi.unlinkTrainerContract(trainerId, id),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', id])
      queryClient.invalidateQueries(['trainers'])
      showToast('Trainer unlinked successfully', 'success')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to unlink trainer', 'error')
    },
  })

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this contract? This action cannot be undone.')) {
      deleteMutation.mutate()
    }
  }

  const handleUnlinkTrainer = (trainerId, trainerName) => {
    if (window.confirm(`Are you sure you want to unlink ${trainerName} from this contract?`)) {
      unlinkTrainerMutation.mutate(trainerId)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Contract not found</p>
        <Button 
          variant="primary" 
          onClick={() => navigate('/contracts')}
          className="mt-4"
        >
          Back to Contracts
        </Button>
      </div>
    )
  }

  const getStatusVariant = (status) => {
    const variants = {
      signed: 'success',
      pending: 'warning',
      draft: 'info',
      rejected: 'danger',
      expired: 'danger',
    }
    return variants[status] || 'gray'
  }

  // Check if contract is signed
  const isContractSigned = contract.status === 'signed' || contract.signed_at

  const tabs = [
    { id: 'overview', label: 'Overview', icon: DocumentTextIcon },
    { id: 'trainers', label: 'Trainers & Teams', icon: UsersIcon },
    { id: 'payments', label: 'Payments', icon: CurrencyDollarIcon },
    { id: 'activity', label: 'Activity', icon: ClockIcon },
    { id: 'files', label: 'Files', icon: DocumentTextIcon },
  ]

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">
              {contract.title || contract.uid || `Contract #${contract.id}`}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <p className="text-gray-600">{contract.uid}</p>
              <Badge variant={getStatusVariant(contract.status)}>
                {contract.status || 'Draft'}
              </Badge>
              {contract.is_expired && (
                <Badge variant="danger">Expired</Badge>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Hide Send Link and Remind buttons for signed contracts */}
            {contract.trainers?.length > 0 && !isContractSigned && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowSendLinkModal(true)}
                >
                  <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                  Send Link
                </Button>
                
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowReminderModal(true)}
                >
                  <BellAlertIcon className="h-4 w-4 mr-1" />
                  Remind
                </Button>
              </>
            )}
            
            {/* Enhanced delete confirmation for signed contracts */}
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                const message = isContractSigned
                  ? '⚠️ WARNING: This contract has been SIGNED. Deleting it will permanently remove all associated data including payments and activity logs.\n\nAre you absolutely sure you want to delete this signed contract?'
                  : 'Are you sure you want to delete this contract? This action cannot be undone.'
                
                if (window.confirm(message)) {
                  handleDelete()
                }
              }}
              loading={deleteMutation.isPending}
            >
              <TrashIcon className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-8">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="pb-8">
          {activeTab === 'overview' && <OverviewTab contract={contract} />}
          {activeTab === 'trainers' && <TrainersTeamsTab contract={contract} />}
          {activeTab === 'payments' && <PaymentsTab contract={contract} />}
          {activeTab === 'activity' && <ActivityTab contract={contract} />}
          {activeTab === 'files' && <FilesTab contract={contract} />}
        </div>

        {/* Back Button */}
        <div className="flex justify-start">
          <Button variant="secondary" onClick={() => navigate('/contracts')}>
            Back to Contracts
          </Button>
        </div>
      </div>

      {/* Modals */}
      <SendSigningLinkModal
        contract={contract}
        trainers={contract.trainers || []}
        isOpen={showSendLinkModal}
        onClose={() => setShowSendLinkModal(false)}
        isReminder={false}
      />
      
      <SendSigningLinkModal
        contract={contract}
        trainers={contract.trainers || []}
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        isReminder={true}
      />

      <LinkTrainerModal
        contract={contract}
        isOpen={showLinkTrainerModal}
        onClose={() => setShowLinkTrainerModal(false)}
        onSuccess={() => queryClient.invalidateQueries(['contract', id, 'full'])}
      />

      <LinkTeamToContractModal
        contract={contract}
        isOpen={showLinkTeamModal}
        onClose={() => setShowLinkTeamModal(false)}
        onSuccess={() => queryClient.invalidateQueries(['contract', id, 'full'])}
      />
    </>
  )
}

// Overview Tab Component
function OverviewTab({ contract }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Basic Details */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Basic Details</h2>
        </Card.Header>
        <Card.Body>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Contract ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{contract.uid}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Title</dt>
              <dd className="mt-1 text-sm text-gray-900">{contract.title || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <Badge variant={contract.status === 'signed' ? 'success' : 'warning'}>
                  {contract.status || 'Draft'}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Start Date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(contract.start_date).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">End Date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(contract.end_date).toLocaleDateString()}
              </dd>
            </div>
            {contract.days_until_expiry !== null && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Days Until Expiry</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {contract.days_until_expiry} days
                </dd>
              </div>
            )}
            
            {/* Contract Duration Information */}
            {(() => {
              const duration = calculateContractDuration(contract)
              if (!duration) return null
              
              return (
                <>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Contract Duration</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {duration.totalDays} days total
                    </dd>
                  </div>
                  
                  {duration.hasStarted && !duration.hasEnded && (
                    <>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Running For</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {duration.daysRunning} days
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Days Until End</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {duration.daysUntilEnd} days remaining
                        </dd>
                      </div>
                    </>
                  )}
                  
                  {duration.hasEnded && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Contract Status</dt>
                      <dd className="mt-1">
                        <Badge variant="danger">Ended</Badge>
                      </dd>
                    </div>
                  )}
                </>
              )
            })()}
          </dl>
        </Card.Body>
      </Card>

      {/* Financial Details */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Financial Details</h2>
        </Card.Header>
        <Card.Body>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Total Amount</dt>
              <dd className="mt-1 text-2xl font-bold text-gray-900">
                €{contract.total_amount?.toLocaleString() || '0'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Payment Frequency</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">
                {contract.payment_frequency || 'N/A'}
              </dd>
            </div>
            {contract.payment_frequency && contract.payment_per_period !== null && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Payment per Period</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  €{contract.payment_per_period?.toLocaleString()}
                </dd>
              </div>
            )}
          </dl>
        </Card.Body>
      </Card>

      {/* Enhanced Signing Details */}
      {contract.signed_at && (
        <Card className="lg:col-span-2 border-2 border-green-500">
          <Card.Header className="bg-green-50">
            <div className="flex items-center gap-2">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-lg font-semibold text-green-900">✅ Contract Signed</h2>
            </div>
            <p className="text-sm text-green-700 mt-1">
              This contract has been signed and is now locked for data integrity.
            </p>
          </Card.Header>
          <Card.Body>
            <dl className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Signed At</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(contract.signed_at).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </dd>
              </div>
              
              {/* Show who signed */}
              {contract.signed_by_trainer_id && contract.trainers && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Signed By</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(() => {
                      const signer = contract.trainers.find(t => t.id === contract.signed_by_trainer_id)
                      return signer ? `${signer.first_name} ${signer.last_name}` : 'Unknown'
                    })()}
                  </dd>
                </div>
              )}
              
              {contract.signature_data && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Signature</dt>
                  <dd className="mt-1">
                    <img
                      src={contract.signature_data}
                      alt="Signature"
                      className="h-16 border rounded bg-white p-1"
                    />
                  </dd>
                </div>
              )}
            </dl>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

// Trainers & Teams Tab Component
function TrainersTeamsTab({ contract }) {
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [showLinkTrainerModal, setShowLinkTrainerModal] = useState(false)
  const [showLinkTeamModal, setShowLinkTeamModal] = useState(false)

  // Check if contract is signed
  const isContractSigned = contract.status === 'signed' || contract.signed_at

  // Unlink trainer mutation
  const unlinkTrainerMutation = useMutation({
    mutationFn: (trainerId) => linksApi.unlinkTrainerContract(trainerId, contract.id),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', contract.id])
      queryClient.invalidateQueries(['trainers'])
      showToast('Trainer unlinked successfully', 'success')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to unlink trainer', 'error')
    },
  })

  // Unlink team mutation
  const unlinkTeamMutation = useMutation({
    mutationFn: (teamId) => linksApi.unlinkTeamContract(teamId, contract.id),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', contract.id])
      queryClient.invalidateQueries(['teams'])
      showToast('Team unlinked successfully', 'success')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to unlink team', 'error')
    },
  })

  const handleUnlinkTrainer = (trainerId, trainerName) => {
    if (window.confirm(`Are you sure you want to unlink ${trainerName} from this contract?`)) {
      unlinkTrainerMutation.mutate(trainerId)
    }
  }

  const handleUnlinkTeam = (teamId, teamName) => {
    if (window.confirm(`Are you sure you want to unlink ${teamName} from this contract?`)) {
      unlinkTeamMutation.mutate(teamId)
    }
  }

  return (
    <>
      {/* Warning banner for signed contracts */}
      {isContractSigned && (
        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
          <div className="flex items-start gap-3">
            <svg className="h-6 w-6 text-yellow-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-yellow-900">Contract is Locked</h3>
              <p className="text-sm text-yellow-700 mt-1">
                This contract has been signed. Trainers and teams cannot be added or removed to maintain contract integrity.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trainers */}
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Linked Trainers ({contract.trainers?.length || 0})
              </h2>
              {!isContractSigned && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowLinkTrainerModal(true)}
                >
                  <UserPlusIcon className="h-4 w-4 mr-1" />
                  Link Trainer
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {contract.trainers && contract.trainers.length > 0 ? (
              <div className="space-y-3">
                {contract.trainers.map((trainer) => (
                  <div
                    key={trainer.id}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <UsersIcon className="h-5 w-5 text-primary-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {trainer.first_name} {trainer.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{trainer.email}</p>
                      {trainer.uid && (
                        <p className="text-xs text-gray-400">{trainer.uid}</p>
                      )}
                    </div>
                    {!isContractSigned && (
                      <button
                        onClick={() => handleUnlinkTrainer(trainer.id, `${trainer.first_name} ${trainer.last_name}`)}
                        className="text-red-600 hover:text-red-700 transition-colors"
                        title="Unlink trainer"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 mb-3">
                  No trainers linked to this contract
                </p>
                {!isContractSigned && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowLinkTrainerModal(true)}
                  >
                    <UserPlusIcon className="h-4 w-4 mr-1" />
                    Link Trainer
                  </Button>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Teams */}
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Linked Teams ({contract.teams?.length || 0})
              </h2>
              {!isContractSigned && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowLinkTeamModal(true)}
                >
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  Link Team
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {contract.teams && contract.teams.length > 0 ? (
              <div className="space-y-3">
                {contract.teams.map((team) => (
                  <div
                    key={team.id}
                    className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg"
                  >
                    <UserGroupIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{team.team_name}</p>
                      {team.category && (
                        <p className="text-xs text-gray-500">Category: {team.category}</p>
                      )}
                      {team.uid && (
                        <p className="text-xs text-gray-400">{team.uid}</p>
                      )}
                    </div>
                    {!isContractSigned && (
                      <button
                        onClick={() => handleUnlinkTeam(team.id, team.team_name)}
                        className="text-red-600 hover:text-red-700 transition-colors"
                        title="Unlink team"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 mb-3">
                  No teams linked to this contract
                </p>
                {!isContractSigned && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowLinkTeamModal(true)}
                  >
                    <UserGroupIcon className="h-4 w-4 mr-1" />
                    Link Team
                  </Button>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      {!isContractSigned && (
        <>
          <LinkTrainerModal
            contract={contract}
            isOpen={showLinkTrainerModal}
            onClose={() => setShowLinkTrainerModal(false)}
            onSuccess={() => queryClient.invalidateQueries(['contract', contract.id, 'full'])}
          />

          <LinkTeamToContractModal
            contract={contract}
            isOpen={showLinkTeamModal}
            onClose={() => setShowLinkTeamModal(false)}
            onSuccess={() => queryClient.invalidateQueries(['contract', contract.id, 'full'])}
          />
        </>
      )}
    </>
  )
}

// Payments Tab Component
function PaymentsTab({ contract }) {
  // For signed contracts OR manual payment frequency, show the actual payments from database
  // For automatic payment frequencies (monthly, quarterly, etc.) on unsigned contracts, show calculated preview
  
  // Manual payments are saved during contract creation, so we need to fetch them even if unsigned
  const isManualPayment = contract.payment_frequency === 'manual'
  
  if (contract.status === 'signed' || isManualPayment) {
    return <PaymentScheduleManager contractId={contract.id} />
  }
  
  // Calculate payments for preview (only for automatic frequencies)
  const previewPayments = calculatePayments(contract)
  
  return (
    <Card>
      <Card.Header>
        <h2 className="text-lg font-semibold text-gray-900">Payment Schedule Preview</h2>
        <p className="text-sm text-gray-600 mt-1">
          These payments will be automatically saved when the contract is signed
        </p>
      </Card.Header>
      <Card.Body>
        <PaymentSchedulePreview payments={previewPayments} isPreview={true} />
      </Card.Body>
    </Card>
  )
}

// Activity Tab Component
function ActivityTab({ contract }) {
  const { data: activityLogs, isLoading } = useQuery({
    queryKey: ['contract', contract.id, 'activity'],
    queryFn: () => activityLogsApi.getEntityActivityLogs('contract', contract.id, 50),
  })

  // Get color for activity type
  const getActivityColor = (action) => {
    const colorMap = {
      'contract_created': 'bg-green-500',
      'create': 'bg-green-500',
      'contract_signed': 'bg-blue-500',
      'sign': 'bg-blue-500',
      'contract_updated': 'bg-yellow-500',
      'update': 'bg-yellow-500',
      'contract_deleted': 'bg-red-500',
      'delete': 'bg-red-500',
      'signing_link_sent': 'bg-purple-500',
      'send': 'bg-purple-500',
      'contract_file_uploaded': 'bg-indigo-500',
      'upload': 'bg-indigo-500',
      'link': 'bg-cyan-500',
      'unlink': 'bg-orange-500',
      'mark_paid': 'bg-emerald-500',
    }
    return colorMap[action] || 'bg-gray-400'
  }

  return (
    <Card>
      <Card.Header>
        <h2 className="text-lg font-semibold text-gray-900">Activity History</h2>
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <div className="text-center py-8">
            <Spinner />
          </div>
        ) : activityLogs && activityLogs.length > 0 ? (
          <div className="space-y-4">
            {activityLogs.map((activity) => (
              <div key={activity.id} className="flex gap-3">
                <div className={`flex-shrink-0 w-2 h-2 mt-2 ${getActivityColor(activity.action)} rounded-full`} />
                <div className="flex-1">
                  <p className="text-sm text-gray-900">
                    {formatActivityMessage(activity)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(activity.created_at).toLocaleString()}
                  </p>
                  {activity.details && Object.keys(activity.details).length > 0 && (
                    <div className="mt-1 text-xs text-gray-600">
                      {activity.details.old_status && activity.details.new_status && (
                        <span>Status: {activity.details.old_status} → {activity.details.new_status}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-8">
            No activity recorded for this contract
          </p>
        )}
      </Card.Body>
    </Card>
  )
}

// Format activity log message
function formatActivityMessage(activity) {
  const { action, entity_type, details, user } = activity
  
  // Build user prefix
  const userPrefix = user
    ? `${user.first_name} ${user.last_name} `
    : 'System '
  
  // Build entity description
  let entityDesc = ''
  switch (entity_type) {
    case 'contract':
      entityDesc = details?.contract_title || details?.title || 'contract'
      break
    case 'trainer':
      entityDesc = details?.trainer_name || 'trainer'
      break
    case 'team':
      entityDesc = details?.team_name || 'team'
      break
    case 'payment':
      entityDesc = 'payment'
      break
    case 'signing_link':
      entityDesc = 'signing link'
      break
    default:
      entityDesc = entity_type
  }
  
  // Build action message
  let actionMsg = ''
  switch (action) {
    case 'contract_created':
    case 'create':
      actionMsg = `${userPrefix}created the contract`
      break
    case 'contract_updated':
    case 'update':
      actionMsg = `${userPrefix}updated the contract`
      break
    case 'contract_deleted':
    case 'delete':
      actionMsg = `${userPrefix}deleted the contract`
      break
    case 'contract_signed':
    case 'sign':
      actionMsg = `${userPrefix}signed the contract`
      break
    case 'signing_link_sent':
    case 'send':
      const count = details?.recipients_count || 1
      actionMsg = `${userPrefix}sent signing link to ${count} trainer${count > 1 ? 's' : ''}`
      break
    case 'contract_file_uploaded':
    case 'upload':
      actionMsg = details?.file_name
        ? `${userPrefix}uploaded file: ${details.file_name}`
        : `${userPrefix}uploaded a file`
      break
    case 'link':
      actionMsg = details?.trainer_name
        ? `${userPrefix}linked trainer ${details.trainer_name}`
        : details?.team_name
        ? `${userPrefix}linked team ${details.team_name}`
        : `${userPrefix}linked ${entityDesc}`
      break
    case 'unlink':
      actionMsg = details?.trainer_name
        ? `${userPrefix}unlinked trainer ${details.trainer_name}`
        : details?.team_name
        ? `${userPrefix}unlinked team ${details.team_name}`
        : `${userPrefix}unlinked ${entityDesc}`
      break
    case 'contract_status_changed':
      actionMsg = `${userPrefix}changed status from ${details?.old_status || 'unknown'} to ${details?.new_status || 'unknown'}`
      break
    case 'mark_paid':
      actionMsg = `${userPrefix}marked payment as paid`
      break
    default:
      actionMsg = `${userPrefix}performed action: ${action}`
  }
  
  return actionMsg
}

// Files Tab Component
function FilesTab({ contract }) {
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadAvailableToTrainer, setUploadAvailableToTrainer] = useState(true)
  const [selectedMainFile, setSelectedMainFile] = useState(null)
  const [viewingPDF, setViewingPDF] = useState(null) // { type: 'main' | 'attachment', url: string, name: string }

  // Fetch attachments
  const { data: attachments, isLoading: loadingAttachments } = useQuery({
    queryKey: ['contract', contract.id, 'attachments'],
    queryFn: () => attachmentsApi.getAttachments(contract.id),
  })

  // Upload attachment mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file, description, availableToTrainer }) =>
      attachmentsApi.uploadAttachment(contract.id, file, description, availableToTrainer),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', contract.id, 'attachments'])
      showToast('File uploaded successfully', 'success')
      setSelectedFile(null)
      setUploadDescription('')
      setUploadAvailableToTrainer(true)
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to upload file', 'error')
    },
  })

  // Update availability mutation
  const updateAvailabilityMutation = useMutation({
    mutationFn: ({ attachmentId, available }) =>
      attachmentsApi.updateAttachmentAvailability(attachmentId, available),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', contract.id, 'attachments'])
      showToast('File availability updated successfully', 'success')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to update availability', 'error')
    },
  })

  // Upload main contract mutation
  const uploadMainMutation = useMutation({
    mutationFn: (file) => contractsApi.uploadContractFile(contract.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', contract.id, 'full'])
      queryClient.invalidateQueries(['contract', contract.id])
      showToast('Main contract uploaded successfully', 'success')
      setSelectedMainFile(null)
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to upload main contract', 'error')
    },
  })

  // Delete attachment mutation
  const deleteMutation = useMutation({
    mutationFn: (attachmentId) => attachmentsApi.deleteAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries(['contract', contract.id, 'attachments'])
      showToast('File deleted successfully', 'success')
    },
    onError: (error) => {
      showToast(error.response?.data?.error || 'Failed to delete file', 'error')
    },
  })

  // Download attachment
  const handleDownloadAttachment = async (attachment) => {
    try {
      const blob = await attachmentsApi.downloadAttachment(attachment.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = attachment.file_name
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      showToast('File downloaded successfully', 'success')
    } catch (error) {
      showToast('Failed to download file', 'error')
    }
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10MB', 'error')
        return
      }
      setSelectedFile(file)
    }
  }

  const handleMainFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      if (!allowedTypes.includes(file.type)) {
        showToast('Please select a PDF or Word document', 'error')
        return
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10MB', 'error')
        return
      }
      setSelectedMainFile(file)
    }
  }

  const handleUpload = () => {
    if (!selectedFile) {
      showToast('Please select a file', 'error')
      return
    }
    uploadMutation.mutate({
      file: selectedFile,
      description: uploadDescription,
      availableToTrainer: uploadAvailableToTrainer
    })
  }

  const handleUploadMain = () => {
    if (!selectedMainFile) {
      showToast('Please select a file', 'error')
      return
    }
    uploadMainMutation.mutate(selectedMainFile)
  }

  const handleDelete = (attachment) => {
    if (window.confirm(`Are you sure you want to delete ${attachment.file_name}?`)) {
      deleteMutation.mutate(attachment.id)
    }
  }

  // Helper function to download contract files
  const handleDownloadContract = async (contractId, filename, isOriginal = false) => {
    try {
      const blob = isOriginal
        ? await contractsApi.downloadOriginalContract(contractId)
        : await contractsApi.downloadContract(contractId)
      
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      showToast('Contract downloaded successfully', 'success')
    } catch (error) {
      console.error('[ERROR] Contract download failed:', error)
      showToast('Failed to download contract', 'error')
    }
  }

  return (
    <>
      <div className="space-y-6">
      {/* Main Contract File */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Main Contract</h2>
        </Card.Header>
        <Card.Body>
          {contract.contract_path ? (
            <div className="space-y-3">
              {/* Signed Contract */}
              {contract.signed_contract_path && (
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                  <DocumentTextIcon className="h-8 w-8 text-green-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {contract.signed_contract_path.split('/').pop()}
                    </p>
                    <p className="text-xs text-green-700">
                      Digitally Signed Contract • Signed on {new Date(contract.signed_at).toLocaleDateString('nl-NL')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setViewingPDF({
                        type: 'signed',
                        url: `${import.meta.env.VITE_API_URL}/contracts/${contract.id}/download`,
                        name: contract.signed_contract_path.split('/').pop()
                      })}
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleDownloadContract(
                        contract.id,
                        contract.signed_contract_path.split('/').pop(),
                        false
                      )}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Original Contract */}
              <div className={`flex items-center gap-3 p-4 rounded-lg ${
                contract.signed_contract_path
                  ? 'bg-gray-50 border border-gray-200'
                  : 'bg-gray-50'
              }`}>
                <DocumentTextIcon className="h-8 w-8 text-primary-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {contract.contract_path.split('/').pop()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {contract.signed_contract_path ? 'Original (Unsigned) • ' : ''}Uploaded {new Date(contract.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setViewingPDF({
                      type: 'original',
                      url: contract.signed_contract_path
                        ? `${import.meta.env.VITE_API_URL}/contracts/${contract.id}/download-original`
                        : `${import.meta.env.VITE_API_URL}/contracts/${contract.id}/download`,
                      name: contract.contract_path.split('/').pop()
                    })}
                  >
                    <EyeIcon className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleDownloadContract(
                      contract.id,
                      contract.contract_path.split('/').pop(),
                      contract.signed_contract_path ? true : false
                    )}
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 mb-4">
                No main contract file uploaded
              </p>
              {selectedMainFile ? (
                <div className="space-y-3">
                  <p className="text-xs text-gray-600">
                    Selected: {selectedMainFile.name} ({(selectedMainFile.size / 1024).toFixed(2)} KB)
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleUploadMain}
                      loading={uploadMainMutation.isPending}
                    >
                      <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
                      Upload Main Contract
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setSelectedMainFile(null)}
                      disabled={uploadMainMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <label
                    htmlFor="main-contract-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 cursor-pointer"
                  >
                    <DocumentArrowUpIcon className="h-4 w-4 mr-1" />
                    Add Main Contract
                  </label>
                  <input
                    id="main-contract-upload"
                    type="file"
                    className="sr-only"
                    accept=".pdf,.doc,.docx"
                    onChange={handleMainFileChange}
                  />
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Additional Attachments */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Additional Files ({attachments?.length || 0})
            </h2>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Upload Area */}
          <div className="mb-6 p-4 border-2 border-dashed border-gray-300 rounded-lg">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <DocumentArrowUpIcon className="h-6 w-6 text-gray-400" />
                <div className="flex-1">
                  <label htmlFor="attachment-upload" className="cursor-pointer">
                    <span className="text-sm font-medium text-primary-600 hover:text-primary-700">
                      Choose file
                    </span>
                    <input
                      id="attachment-upload"
                      type="file"
                      className="sr-only"
                      onChange={handleFileChange}
                      disabled={uploadMutation.isPending}
                    />
                  </label>
                  <span className="text-sm text-gray-500 ml-2">or drag and drop</span>
                  {selectedFile && (
                    <p className="text-xs text-gray-600 mt-1">
                      Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </p>
                  )}
                </div>
              </div>
              
              {selectedFile && (
                <>
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={uploadAvailableToTrainer}
                      onChange={(e) => setUploadAvailableToTrainer(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      Make available to trainer for signing
                    </span>
                  </label>
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleUpload}
                      loading={uploadMutation.isPending}
                    >
                      Upload
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedFile(null)
                        setUploadDescription('')
                        setUploadAvailableToTrainer(true)
                      }}
                      disabled={uploadMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Attachments List */}
          {loadingAttachments ? (
            <div className="text-center py-8">
              <Spinner />
            </div>
          ) : attachments && attachments.length > 0 ? (
            <div className="space-y-3">
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <DocumentTextIcon className="h-6 w-6 text-gray-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {attachment.file_name}
                    </p>
                    {attachment.description && (
                      <p className="text-xs text-gray-600 mt-1">{attachment.description}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Uploaded {new Date(attachment.uploaded_at).toLocaleDateString()}
                      {attachment.file_size && ` • ${(attachment.file_size / 1024).toFixed(2)} KB`}
                      {attachment.uploaded_by_name && ` • by ${attachment.uploaded_by_name}`}
                    </p>
                    {attachment.available_to_trainer ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1">
                        <EyeIcon className="h-3 w-3 mr-1" />
                        Visible to trainer
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 mt-1">
                        <EyeSlashIcon className="h-3 w-3 mr-1" />
                        Admin only
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateAvailabilityMutation.mutate({
                        attachmentId: attachment.id,
                        available: !attachment.available_to_trainer
                      })}
                      className={`p-2 rounded-md transition-colors ${
                        attachment.available_to_trainer
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={attachment.available_to_trainer
                        ? 'Visible to trainer - Click to hide'
                        : 'Hidden from trainer - Click to show'
                      }
                      disabled={updateAvailabilityMutation.isPending}
                    >
                      {attachment.available_to_trainer ? (
                        <EyeIcon className="h-5 w-5" />
                      ) : (
                        <EyeSlashIcon className="h-5 w-5" />
                      )}
                    </button>
                    {attachment.file_name.toLowerCase().endsWith('.pdf') && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setViewingPDF({
                          type: 'attachment',
                          url: `${import.meta.env.VITE_API_URL}/attachments/${attachment.id}/download`,
                          name: attachment.file_name
                        })}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleDownloadAttachment(attachment)}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(attachment)}
                      loading={deleteMutation.isPending}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-8">
              No additional files attached
            </p>
          )}
        </Card.Body>
      </Card>
    </div>

    {/* PDF Viewer Modal */}
    {viewingPDF && (
      <div className="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-75 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{viewingPDF.name}</h3>
            <button
              onClick={() => setViewingPDF(null)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* PDF Viewer */}
          <div className="flex-1 overflow-hidden">
            <PDFViewer url={viewingPDF.url} className="h-full" />
          </div>
        </div>
      </div>
    )}
  </>
  )
}